import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// ── Translations ──────────────────────────────────────────────────────────────
// All navigation and common UI strings translated into every supported language.
// Story content is always generated in the user's chosen language by Claude/GPT.
// This covers the app chrome: sidebar, navbar, buttons, common labels.

const resources = {
  en: {
    translation: {
      nav: {
        home: 'Home',
        generate: 'Generate Story',
        library: 'Story Library',
        children: 'Child Profiles',
        approvalQueue: 'Approval Queue',
        reports: 'Reading Reports',
        series: 'Story Series',
        community: 'Community',
        settings: 'Settings',
        giftStory: 'Gift a Story',
        upgradePlan: 'Upgrade Plan',
        upgradeToFamily: 'Upgrade to Family',
        affiliate: 'Affiliate Program',
        help: 'Help',
        signOut: 'Sign Out',
      },
      navbar: {
        blog: 'Blog',
        about: 'About',
        faq: 'FAQ',
        pricing: 'Pricing',
        affiliates: 'Affiliates',
        giftStory: '🎁 Gift a Story',
        dashboard: 'Dashboard',
        login: 'Log In',
        register: 'Get Started',
      },
      common: {
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        back: 'Back',
        next: 'Next',
        loading: 'Loading…',
        generate: 'Generate',
        approve: 'Approve',
        reject: 'Reject',
        retry: 'Try Again',
        noResults: 'No results found.',
        learnMore: 'Learn More',
        viewAll: 'View All',
        close: 'Close',
        confirm: 'Confirm',
        freePlan: 'Free',
        basicPlan: 'Basic',
        familyPlan: 'Family',
      },
    },
  },

  es: {
    translation: {
      nav: {
        home: 'Inicio',
        generate: 'Crear Historia',
        library: 'Biblioteca',
        children: 'Perfiles Infantiles',
        approvalQueue: 'Cola de Aprobación',
        reports: 'Informes de Lectura',
        series: 'Serie de Historias',
        community: 'Comunidad',
        settings: 'Configuración',
        giftStory: 'Regalar una Historia',
        upgradePlan: 'Mejorar Plan',
        upgradeToFamily: 'Cambiar a Familiar',
        affiliate: 'Programa de Afiliados',
        help: 'Ayuda',
        signOut: 'Cerrar Sesión',
      },
      navbar: {
        blog: 'Blog',
        about: 'Acerca de',
        faq: 'Preguntas',
        pricing: 'Precios',
        affiliates: 'Afiliados',
        giftStory: '🎁 Regalar Historia',
        dashboard: 'Panel',
        login: 'Iniciar Sesión',
        register: 'Empezar Gratis',
      },
      common: {
        save: 'Guardar',
        cancel: 'Cancelar',
        delete: 'Eliminar',
        edit: 'Editar',
        back: 'Atrás',
        next: 'Siguiente',
        loading: 'Cargando…',
        generate: 'Generar',
        approve: 'Aprobar',
        reject: 'Rechazar',
        retry: 'Intentar de Nuevo',
        noResults: 'Sin resultados.',
        learnMore: 'Saber Más',
        viewAll: 'Ver Todo',
        close: 'Cerrar',
        confirm: 'Confirmar',
        freePlan: 'Gratuito',
        basicPlan: 'Básico',
        familyPlan: 'Familiar',
      },
    },
  },

  fr: {
    translation: {
      nav: {
        home: 'Accueil',
        generate: 'Créer une Histoire',
        library: 'Bibliothèque',
        children: 'Profils Enfants',
        approvalQueue: "File d'Approbation",
        reports: 'Rapports de Lecture',
        series: "Séries d'Histoires",
        community: 'Communauté',
        settings: 'Paramètres',
        giftStory: 'Offrir une Histoire',
        upgradePlan: 'Améliorer le Plan',
        upgradeToFamily: 'Passer au Famille',
        affiliate: "Programme d'Affiliation",
        help: 'Aide',
        signOut: 'Déconnexion',
      },
      navbar: {
        blog: 'Blog',
        about: 'À Propos',
        faq: 'FAQ',
        pricing: 'Tarifs',
        affiliates: 'Affiliés',
        giftStory: '🎁 Offrir une Histoire',
        dashboard: 'Tableau de Bord',
        login: 'Connexion',
        register: 'Commencer',
      },
      common: {
        save: 'Enregistrer',
        cancel: 'Annuler',
        delete: 'Supprimer',
        edit: 'Modifier',
        back: 'Retour',
        next: 'Suivant',
        loading: 'Chargement…',
        generate: 'Générer',
        approve: 'Approuver',
        reject: 'Rejeter',
        retry: 'Réessayer',
        noResults: 'Aucun résultat.',
        learnMore: 'En Savoir Plus',
        viewAll: 'Voir Tout',
        close: 'Fermer',
        confirm: 'Confirmer',
        freePlan: 'Gratuit',
        basicPlan: 'Basique',
        familyPlan: 'Famille',
      },
    },
  },

  de: {
    translation: {
      nav: {
        home: 'Startseite',
        generate: 'Geschichte Erstellen',
        library: 'Geschichtenbibliothek',
        children: 'Kinderprofile',
        approvalQueue: 'Genehmigungsqueue',
        reports: 'Leseberichte',
        series: 'Geschichtenreihe',
        community: 'Gemeinschaft',
        settings: 'Einstellungen',
        giftStory: 'Geschichte Verschenken',
        upgradePlan: 'Plan Upgraden',
        upgradeToFamily: 'Auf Familie Upgraden',
        affiliate: 'Partnerprogramm',
        help: 'Hilfe',
        signOut: 'Abmelden',
      },
      navbar: {
        blog: 'Blog',
        about: 'Über Uns',
        faq: 'FAQ',
        pricing: 'Preise',
        affiliates: 'Partner',
        giftStory: '🎁 Geschichte Schenken',
        dashboard: 'Dashboard',
        login: 'Anmelden',
        register: 'Kostenlos Starten',
      },
      common: {
        save: 'Speichern',
        cancel: 'Abbrechen',
        delete: 'Löschen',
        edit: 'Bearbeiten',
        back: 'Zurück',
        next: 'Weiter',
        loading: 'Wird geladen…',
        generate: 'Generieren',
        approve: 'Genehmigen',
        reject: 'Ablehnen',
        retry: 'Erneut Versuchen',
        noResults: 'Keine Ergebnisse.',
        learnMore: 'Mehr Erfahren',
        viewAll: 'Alle Anzeigen',
        close: 'Schließen',
        confirm: 'Bestätigen',
        freePlan: 'Kostenlos',
        basicPlan: 'Basis',
        familyPlan: 'Familie',
      },
    },
  },

  pt: {
    translation: {
      nav: {
        home: 'Início',
        generate: 'Criar História',
        library: 'Biblioteca de Histórias',
        children: 'Perfis das Crianças',
        approvalQueue: 'Fila de Aprovação',
        reports: 'Relatórios de Leitura',
        series: 'Série de Histórias',
        community: 'Comunidade',
        settings: 'Configurações',
        giftStory: 'Presentear uma História',
        upgradePlan: 'Melhorar Plano',
        upgradeToFamily: 'Mudar para Família',
        affiliate: 'Programa de Afiliados',
        help: 'Ajuda',
        signOut: 'Sair',
      },
      navbar: {
        blog: 'Blog',
        about: 'Sobre Nós',
        faq: 'FAQ',
        pricing: 'Preços',
        affiliates: 'Afiliados',
        giftStory: '🎁 Oferecer uma História',
        dashboard: 'Painel',
        login: 'Entrar',
        register: 'Começar Grátis',
      },
      common: {
        save: 'Guardar',
        cancel: 'Cancelar',
        delete: 'Eliminar',
        edit: 'Editar',
        back: 'Voltar',
        next: 'Próximo',
        loading: 'A carregar…',
        generate: 'Gerar',
        approve: 'Aprovar',
        reject: 'Rejeitar',
        retry: 'Tentar Novamente',
        noResults: 'Sem resultados.',
        learnMore: 'Saber Mais',
        viewAll: 'Ver Tudo',
        close: 'Fechar',
        confirm: 'Confirmar',
        freePlan: 'Gratuito',
        basicPlan: 'Básico',
        familyPlan: 'Família',
      },
    },
  },

  it: {
    translation: {
      nav: {
        home: 'Home',
        generate: 'Crea Storia',
        library: 'Libreria Storie',
        children: 'Profili Bambini',
        approvalQueue: 'Coda Approvazione',
        reports: 'Report di Lettura',
        series: 'Serie di Storie',
        community: 'Comunità',
        settings: 'Impostazioni',
        giftStory: 'Regala una Storia',
        upgradePlan: 'Aggiorna Piano',
        upgradeToFamily: 'Passa a Famiglia',
        affiliate: 'Programma Affiliati',
        help: 'Aiuto',
        signOut: 'Esci',
      },
      navbar: {
        blog: 'Blog',
        about: 'Chi Siamo',
        faq: 'FAQ',
        pricing: 'Prezzi',
        affiliates: 'Affiliati',
        giftStory: '🎁 Regala una Storia',
        dashboard: 'Dashboard',
        login: 'Accedi',
        register: 'Inizia Gratis',
      },
      common: {
        save: 'Salva',
        cancel: 'Annulla',
        delete: 'Elimina',
        edit: 'Modifica',
        back: 'Indietro',
        next: 'Avanti',
        loading: 'Caricamento…',
        generate: 'Genera',
        approve: 'Approva',
        reject: 'Rifiuta',
        retry: 'Riprova',
        noResults: 'Nessun risultato.',
        learnMore: 'Scopri di Più',
        viewAll: 'Vedi Tutto',
        close: 'Chiudi',
        confirm: 'Conferma',
        freePlan: 'Gratuito',
        basicPlan: 'Base',
        familyPlan: 'Famiglia',
      },
    },
  },

  hi: {
    translation: {
      nav: {
        home: 'होम',
        generate: 'कहानी बनाएं',
        library: 'कहानी संग्रह',
        children: 'बच्चों की प्रोफ़ाइल',
        approvalQueue: 'स्वीकृति कतार',
        reports: 'पठन रिपोर्ट',
        series: 'कहानी श्रृंखला',
        community: 'समुदाय',
        settings: 'सेटिंग्स',
        giftStory: 'कहानी उपहार दें',
        upgradePlan: 'प्लान अपग्रेड करें',
        upgradeToFamily: 'फ़ैमिली प्लान लें',
        affiliate: 'एफ़िलिएट प्रोग्राम',
        help: 'सहायता',
        signOut: 'साइन आउट',
      },
      navbar: {
        blog: 'ब्लॉग',
        about: 'हमारे बारे में',
        faq: 'सवाल-जवाब',
        pricing: 'मूल्य निर्धारण',
        affiliates: 'एफ़िलिएट',
        giftStory: '🎁 कहानी उपहार दें',
        dashboard: 'डैशबोर्ड',
        login: 'लॉगिन',
        register: 'शुरू करें',
      },
      common: {
        save: 'सहेजें',
        cancel: 'रद्द करें',
        delete: 'हटाएं',
        edit: 'संपादित करें',
        back: 'वापस',
        next: 'आगे',
        loading: 'लोड हो रहा है…',
        generate: 'बनाएं',
        approve: 'स्वीकृत करें',
        reject: 'अस्वीकार करें',
        retry: 'पुनः प्रयास करें',
        noResults: 'कोई परिणाम नहीं।',
        learnMore: 'और जानें',
        viewAll: 'सभी देखें',
        close: 'बंद करें',
        confirm: 'पुष्टि करें',
        freePlan: 'मुफ़्त',
        basicPlan: 'बेसिक',
        familyPlan: 'फ़ैमिली',
      },
    },
  },

  gu: {
    translation: {
      nav: {
        home: 'ઘર',
        generate: 'વાર્તા બનાવો',
        library: 'વાર્તા ભંડાર',
        children: 'બાળ પ્રોફાઇલ',
        approvalQueue: 'મંજૂરી કતાર',
        reports: 'વાંચન અહેવાલ',
        series: 'વાર્તા શ્રૃંખલા',
        community: 'સમુદાય',
        settings: 'સેટિંગ્સ',
        giftStory: 'વાર્તા ભેટ આપો',
        upgradePlan: 'પ્લાન અપગ્રેડ કરો',
        upgradeToFamily: 'ફેમિલી પ્લાન લો',
        affiliate: 'સહભાગી કાર્યક્રમ',
        help: 'મદદ',
        signOut: 'સાઇન આઉટ',
      },
      navbar: {
        blog: 'બ્લૉગ',
        about: 'અમારા વિશે',
        faq: 'સવાલ-જવાબ',
        pricing: 'કિંમત',
        affiliates: 'સહભાગી',
        giftStory: '🎁 વાર્તા ભેટ',
        dashboard: 'ડૅશબોર્ડ',
        login: 'લૉગિન',
        register: 'શરૂ કરો',
      },
      common: {
        save: 'સાચવો',
        cancel: 'રદ કરો',
        delete: 'ભૂંસો',
        edit: 'સંપાદન',
        back: 'પાછળ',
        next: 'આગળ',
        loading: 'લોડ થઈ રહ્યું છે…',
        generate: 'બનાવો',
        approve: 'મંજૂર કરો',
        reject: 'નકારો',
        retry: 'ફરી પ્રયાસ',
        noResults: 'કોઈ પરિણામ નહીં.',
        learnMore: 'વધુ જાણો',
        viewAll: 'બધું જુઓ',
        close: 'બંધ કરો',
        confirm: 'પુષ્ટિ',
        freePlan: 'મફત',
        basicPlan: 'બેઝિક',
        familyPlan: 'ફેમિલી',
      },
    },
  },

  ar: {
    translation: {
      nav: {
        home: 'الرئيسية',
        generate: 'إنشاء قصة',
        library: 'مكتبة القصص',
        children: 'ملفات الأطفال',
        approvalQueue: 'قائمة الموافقة',
        reports: 'تقارير القراءة',
        series: 'سلسلة القصص',
        community: 'المجتمع',
        settings: 'الإعدادات',
        giftStory: 'إهداء قصة',
        upgradePlan: 'ترقية الخطة',
        upgradeToFamily: 'الانتقال إلى عائلة',
        affiliate: 'برنامج الشركاء',
        help: 'المساعدة',
        signOut: 'تسجيل الخروج',
      },
      navbar: {
        blog: 'المدونة',
        about: 'من نحن',
        faq: 'الأسئلة الشائعة',
        pricing: 'الأسعار',
        affiliates: 'الشركاء',
        giftStory: '🎁 إهداء قصة',
        dashboard: 'لوحة التحكم',
        login: 'تسجيل الدخول',
        register: 'ابدأ مجاناً',
      },
      common: {
        save: 'حفظ',
        cancel: 'إلغاء',
        delete: 'حذف',
        edit: 'تعديل',
        back: 'رجوع',
        next: 'التالي',
        loading: 'جارٍ التحميل…',
        generate: 'إنشاء',
        approve: 'موافقة',
        reject: 'رفض',
        retry: 'حاول مرة أخرى',
        noResults: 'لا توجد نتائج.',
        learnMore: 'اعرف المزيد',
        viewAll: 'عرض الكل',
        close: 'إغلاق',
        confirm: 'تأكيد',
        freePlan: 'مجاني',
        basicPlan: 'أساسي',
        familyPlan: 'عائلي',
      },
    },
  },

  zh: {
    translation: {
      nav: {
        home: '主页',
        generate: '生成故事',
        library: '故事图书馆',
        children: '儿童档案',
        approvalQueue: '审批队列',
        reports: '阅读报告',
        series: '故事系列',
        community: '社区',
        settings: '设置',
        giftStory: '赠送故事',
        upgradePlan: '升级套餐',
        upgradeToFamily: '升级家庭版',
        affiliate: '联盟计划',
        help: '帮助',
        signOut: '退出登录',
      },
      navbar: {
        blog: '博客',
        about: '关于我们',
        faq: '常见问题',
        pricing: '价格',
        affiliates: '联盟',
        giftStory: '🎁 赠送故事',
        dashboard: '仪表板',
        login: '登录',
        register: '免费开始',
      },
      common: {
        save: '保存',
        cancel: '取消',
        delete: '删除',
        edit: '编辑',
        back: '返回',
        next: '下一步',
        loading: '加载中…',
        generate: '生成',
        approve: '批准',
        reject: '拒绝',
        retry: '重试',
        noResults: '没有找到结果。',
        learnMore: '了解更多',
        viewAll: '查看全部',
        close: '关闭',
        confirm: '确认',
        freePlan: '免费',
        basicPlan: '基础版',
        familyPlan: '家庭版',
      },
    },
  },

  ja: {
    translation: {
      nav: {
        home: 'ホーム',
        generate: 'お話を作る',
        library: 'お話の本棚',
        children: 'お子様プロフィール',
        approvalQueue: '承認キュー',
        reports: '読書レポート',
        series: 'お話シリーズ',
        community: 'コミュニティ',
        settings: '設定',
        giftStory: 'お話をプレゼント',
        upgradePlan: 'プランをアップグレード',
        upgradeToFamily: 'ファミリーへアップグレード',
        affiliate: 'アフィリエイトプログラム',
        help: 'ヘルプ',
        signOut: 'サインアウト',
      },
      navbar: {
        blog: 'ブログ',
        about: '私たちについて',
        faq: 'よくある質問',
        pricing: '料金',
        affiliates: 'アフィリエイト',
        giftStory: '🎁 お話をプレゼント',
        dashboard: 'ダッシュボード',
        login: 'ログイン',
        register: '無料で始める',
      },
      common: {
        save: '保存',
        cancel: 'キャンセル',
        delete: '削除',
        edit: '編集',
        back: '戻る',
        next: '次へ',
        loading: '読み込み中…',
        generate: '生成',
        approve: '承認',
        reject: '却下',
        retry: 'もう一度試す',
        noResults: '結果が見つかりません。',
        learnMore: 'もっと詳しく',
        viewAll: 'すべて表示',
        close: '閉じる',
        confirm: '確認',
        freePlan: '無料',
        basicPlan: 'ベーシック',
        familyPlan: 'ファミリー',
      },
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    // Map language codes that may come from the browser to our supported codes.
    // e.g. 'zh-CN' → 'zh', 'pt-BR' → 'pt'
    load: 'languageOnly',
    supportedLngs: ['en', 'es', 'fr', 'de', 'pt', 'it', 'hi', 'gu', 'ar', 'zh', 'ja'],
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    detection: {
      // Detect from localStorage first so user's choice persists, then browser
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'dreamyLang',
    },
  });

export default i18n;
